// SingletonMiniPlayer — a tiny floating pill at the bottom of the
// screen that appears whenever ANY one-shot TTS (read-aloud button,
// devotional, morning briefing, pioneer card) is playing through the
// `lib/singleAudio` singleton. Lets the user pause/resume/stop from
// any page without scrolling back to the card that started it.
//
// This is NOT the chapter reader UI — that one has its own AudioBar.
// The chapter reader self-identifies as `id:'chapter-reader'` on the
// `hw:read-aloud:start` bus and we hide ourselves whenever it takes
// over so the two players never stack.
//
// Design:
//  - Pill sits 116px above the bottom (clears BottomNav + iOS safe
//    area). Right-aligned, never blocks center content.
//  - Subscribes to the singleton's native `play`/`pause`/`ended`/
//    `error` events via onPlaybackEvent → drives visibility + state.
//  - Captures a friendly label from `hw:read-aloud:start` detail.
import React, { useEffect, useState } from "react";
import { Play, Pause, Stop, SpeakerHigh, SkipForward, Sparkle, Microphone } from "@phosphor-icons/react";
import { onPlaybackEvent, getSingletonAudio, stopOnce } from "../lib/singleAudio";

const BUS_EVENT = "hw:read-aloud:start";

export default function SingletonMiniPlayer() {
  const [visible, setVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [label, setLabel] = useState("Read aloud");
  // True while the morning "Play today's briefing" sequence owns the
  // singleton. Used to show a Skip-to-next arrow on the mini-player
  // so the user can jump from Devotional → On this day → Something
  // good → Someone you should know without going back to the source
  // button. PlayAllTodayButton listens for `hw:briefing:skip` on the
  // window bus and advances its segment cursor.
  const [isBriefing, setIsBriefing] = useState(false);
  // Track which surface currently owns the singleton. When the
  // chapter reader claims it (via id="chapter-reader"), we suppress
  // every subsequent native `play` event so we don't pop a second
  // mini-player below the reader's built-in AudioBar. User saw both
  // players on /apocrypha/bel_dragon/1.
  const suppressRef = React.useRef(false);

  // Capture the label from the most recent BUS_EVENT — every caller
  // dispatches `{ id, label }` so we can show what's playing.
  useEffect(() => {
    const onStart = (e) => {
      const id = e?.detail?.id;
      const lbl = e?.detail?.label;
      // Chapter reader has its own AudioBar; bail so we don't
      // double-stack two players.
      if (id === "chapter-reader") {
        suppressRef.current = true;
        setVisible(false);
        return;
      }
      // A new non-chapter source claimed the singleton — re-enable
      // the pill so subsequent native `play` events show it.
      suppressRef.current = false;
      setIsBriefing(id === "play-all-today");
      if (lbl) setLabel(lbl);
    };
    window.addEventListener(BUS_EVENT, onStart);
    return () => window.removeEventListener(BUS_EVENT, onStart);
  }, []);

  // Mirror the singleton's playback state into our UI.
  useEffect(() => {
    const off = onPlaybackEvent((evt) => {
      // While the chapter reader owns the singleton, every native
      // play/pause/ended is its own — INCLUDING the per-verse `ended`
      // that fires between verses. Never lift suppression here (that
      // was the "two players stacked on the Bible page" bug); only a
      // new non-chapter source claiming the bus (onStart above) may
      // re-enable the pill.
      if (suppressRef.current) return;
      if (evt === "play")  { setVisible(true);  setIsPaused(false); }
      if (evt === "pause") { setIsPaused(true); }
      if (evt === "ended" || evt === "error") { setVisible(false); setIsPaused(false); }
    });
    return off;
  }, []);

  if (!visible) return null;

  const togglePause = () => {
    const el = getSingletonAudio();
    try {
      if (el.paused) {
        const p = el.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } else {
        el.pause();
      }
    } catch (_) {}
  };

  const handleStop = () => {
    stopOnce();
    setVisible(false);
    setIsPaused(false);
    setIsBriefing(false);
  };

  const handleSkipNext = () => {
    try { window.dispatchEvent(new CustomEvent("hw:briefing:skip")); } catch (_) {}
  };

  return (
    <div
      data-testid="singleton-mini-player"
      role="region"
      aria-label="Now playing"
      className="fixed right-4 z-40 select-none"
      style={{ bottom: "calc(env(safe-area-inset-bottom) + 7.25rem)" }}
    >
      <div className="glass rounded-full border border-cream-300/60 shadow-[0_8px_24px_rgba(0,0,0,0.22)] pl-3 pr-2 py-2 flex items-center gap-2 max-w-[78vw]">
        <SpeakerHigh size={14} weight="duotone" className="text-forest shrink-0" />
        <div className="min-w-0">
          <div className="text-[8px] tracking-[0.22em] uppercase font-bold text-muted_ink leading-none">
            Now playing
          </div>
          <div className="text-[11px] font-semibold text-ink truncate leading-tight mt-0.5" data-testid="singleton-mini-player-label">
            {label}
          </div>
        </div>
        <button
          type="button"
          onClick={togglePause}
          aria-label={isPaused ? "Resume" : "Pause"}
          data-testid="singleton-mini-player-toggle"
          className="shrink-0 ml-1 w-8 h-8 text-forest hover:text-terracotta grid place-items-center hover:bg-forest/90 active:scale-95 transition"
        >
          {isPaused ? <Play size={12} weight="fill" /> : <Pause size={12} weight="bold" />}
        </button>
        {isBriefing && (
          <button
            type="button"
            onClick={handleSkipNext}
            aria-label="Skip to next segment"
            data-testid="singleton-mini-player-skip"
            title="Skip to next briefing segment"
            className="shrink-0 w-8 h-8 text-ink/70 text-ink grid place-items-center hover:bg-cream-300/80 active:scale-95 transition"
          >
            <SkipForward size={12} weight="bold" />
          </button>
        )}
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent("holywords:open-ai"))}
          aria-label="Ask AI"
          title="Ask AI"
          data-testid="singleton-mini-player-ai"
          className="shrink-0 w-8 h-8 text-[#C9A24E] grid place-items-center hover:bg-cream-300/80 active:scale-95 transition"
        >
          <Sparkle size={12} weight="fill" />
        </button>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent("holywords:open-voice"))}
          aria-label="Voice commands"
          title="Voice commands"
          data-testid="singleton-mini-player-voice"
          className="shrink-0 w-8 h-8 text-terracotta grid place-items-center hover:bg-cream-300/80 active:scale-95 transition"
        >
          <Microphone size={12} weight="fill" />
        </button>
        <button
          type="button"
          onClick={handleStop}
          aria-label="Stop"
          data-testid="singleton-mini-player-stop"
          className="shrink-0 w-8 h-8 text-ink/70 text-ink grid place-items-center hover:bg-cream-300/80 active:scale-95 transition"
        >
          <Stop size={12} weight="fill" />
        </button>
      </div>
    </div>
  );
}
